import React, { useEffect } from 'react';

interface ToastContainerProps {
  position?: string;
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
  'aria-label'?: string;
}

const positionMap: Record<string, string> = {
  'top-start': 'top-0 left-0',
  'top-center': 'top-0 left-1/2 -translate-x-1/2',
  'top-end': 'top-0 right-0',
  'middle-center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
  'bottom-start': 'bottom-0 left-0',
  'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2',
  'bottom-end': 'bottom-0 right-0',
};

function ToastContainer({ position, style, className = '', children, ...rest }: ToastContainerProps) {
  const posClass = position ? positionMap[position] || '' : '';
  return (
    <div className={`fixed z-50 p-4 ${posClass} ${className}`.trim()} style={style} {...rest}>
      {children}
    </div>
  );
}

interface ToastProps {
  show?: boolean;
  onClose?: () => void;
  autohide?: boolean;
  delay?: number;
  bg?: string;
  className?: string;
  children?: React.ReactNode;
}

function ToastBase({ show, onClose, autohide, delay = 5000, bg, className = '', children }: ToastProps) {
  useEffect(() => {
    if (show && autohide) {
      const timer = setTimeout(() => onClose?.(), delay);
      return () => clearTimeout(timer);
    }
  }, [show, autohide, delay, onClose]);

  if (!show) return null;

  const bgClass = bg ? `bg-${bg}` : '';
  return (
    <div className={`rounded-lg shadow-lg ${bgClass} ${className}`.trim()} role="alert" aria-live="assertive" aria-atomic="true">
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child as React.ReactElement<any>, { _onClose: onClose });
      })}
    </div>
  );
}

interface SubProps {
  className?: string;
  children?: React.ReactNode;
  _onClose?: () => void;
}

function ToastHeader({ className = '', children, _onClose }: SubProps) {
  return (
    <div className={`flex items-center justify-between p-2 ${className}`.trim()}>
      {children}
      <button type="button" className="btn btn-sm btn-circle btn-ghost" aria-label="Close" onClick={_onClose}>✕</button>
    </div>
  );
}

function ToastBody({ className = '', children }: { className?: string; children?: React.ReactNode }) {
  return <div className={`p-3 ${className}`.trim()}>{children}</div>;
}

const Toast = Object.assign(ToastBase, {
  Header: ToastHeader,
  Body: ToastBody,
});

export { ToastContainer };
export default Toast;
